/**
 * Native-fullscreen interception.
 *
 * When the user (or the iOS Safari quirky `webkitbeginfullscreen`) puts the
 * <video> into fullscreen, we transparently redirect to put the surrounding
 * `stage` element into fullscreen instead. That way the marker-strip,
 * banner, and popover stay visible.
 *
 * In stage-fullscreen we additionally hide the native controls so the
 * editor look-and-feel is uniform.
 */

export function attachFullscreenInterceptor(video, stage, onLayoutChange) {
  // Two fullscreen modes coexist now (the redirect-trick was removed because
  // it lost the click's user-gesture and the browser denied the second
  // requestFullscreen — visible as a "flicker" into fullscreen and right
  // back out):
  //   - Native button on the video element  → only the <video> goes
  //                                            fullscreen (user expectation)
  //   - F key                                → stage.requestFullscreen()
  //                                            (editor strips visible)
  // We still tag the stage when IT enters fullscreen, so editor CSS kicks in.

  // iOS Safari quirk: webkitbeginfullscreen on <video> takes over the
  // whole screen with iOS native player. For our editor mode we still
  // want to redirect to stage-fullscreen on iOS where the user DID
  // initiate from a tap (gesture is preserved across the same call).
  const onWebkitBegin = () => {
    try { document.exitFullscreen?.(); } catch { /* noop */ }
    try { stage.requestFullscreen?.(); } catch { /* noop */ }
  };
  video.addEventListener('webkitbeginfullscreen', onWebkitBegin);

  let savedControls = video.hasAttribute('controls');
  const onFullscreenChange = () => {
    if (document.fullscreenElement === stage) {
      savedControls = video.hasAttribute('controls');
      video.removeAttribute('controls');
      stage.classList.add('zap-pe-fullscreen');
    } else {
      if (savedControls) video.setAttribute('controls', '');
      stage.classList.remove('zap-pe-fullscreen');
    }
    onLayoutChange?.();
  };
  document.addEventListener('fullscreenchange', onFullscreenChange);

  // Click on the video in stage-fullscreen toggles play/pause (native controls are gone).
  const onVideoClick = (e) => {
    if (!stage.classList.contains('zap-pe-fullscreen')) return;
    e.preventDefault();
    video.paused ? video.play() : video.pause();
  };
  video.addEventListener('click', onVideoClick);

  return () => {
    video.removeEventListener('webkitbeginfullscreen', onWebkitBegin);
    document.removeEventListener('fullscreenchange', onFullscreenChange);
    video.removeEventListener('click', onVideoClick);
  };
}
